import cx from "classnames";
import React, { useEffect, useState } from "react";
import { ToastTypes } from "../../../../contexts/toastr.context";
import { useToast } from "../../../../hooks/use-toast.hook";
import { InvoicePreference } from "../../../../types/invoice-preference/invoice-preference.type";
import { Button, Loader, TextInput } from "../../../../_components";
import { Checkbox } from "../../../../_components/form-controls/checkbox.component";
import LoginRequiredViewComponent from "../../../../_components/login-required-view.component";
import WidgetState from "../../../../_components/molecule/widget-state-view";
import Label from "../../../../_components/typography/label.typography";
import { SubscriptionUtils } from "../../../../_utils/subscription/subscription-util";
import Validator from "../../../../_utils/validator";
import { useAppService } from "../../../../hooks/use-app-service";

type Status = "idle" | "loading" | "success" | "error";

interface CommunityInvoiceProps {
  user: any;
  invoicePreference: {
    preference: InvoicePreference;
    status: Status;
  };
  /**
   * Update invoice preference on backend and store in redux
   */
  updateInvoicePreference: (
    payload: any,
    onSuccess: (_: InvoicePreference) => void,
    onFailed: (_: any) => void
  ) => void;
}
/**
 * Page for community invoice settings for subscriptions
 */
function CommunityInvoicePage({
  user,
  invoicePreference,
  updateInvoicePreference = (
    payload: any,
    onSuccess: (_: InvoicePreference) => void,
    onFailed: (_: any) => void
  ) => {},
}: CommunityInvoiceProps) {
  const { analyticsService } = useAppService();

  const [autoSendInvoice, setAutoSendInvoice] = useState<boolean>(false);
  const [invoicePrefix, setInvoicePrefix] = useState<string>("");
  const [invoiceSeries, setInvoiceSeries] = useState<string>("11111");

  const [organisationName, setOrganisationName] = useState<string>("");
  const [addressLine1, setAddressLine1] = useState<string>("");
  const [addressLine2, setAddressLine2] = useState<string>("");
  const [state, setState] = useState<string>("");
  const [country, setCountry] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [taxName, setTaxName] = useState<string>("");
  const [taxId, setTaxId] = useState<string>("");
  const [taxPercentage, setTaxPercentage] = useState<number>(0);
  const [saveStatus, setSaveStatus] = useState<Status>("idle");

  const toast = useToast();

  // Initialize invoice preferences
  useEffect(() => {
    if (
      !invoicePreference ||
      !Object.keys(invoicePreference?.preference).length
    ) {
      return;
    }
    const preference = invoicePreference?.preference;

    if (preference.autoSendInvoice) {
      setAutoSendInvoice(preference.autoSendInvoice);
    }
    if (preference.invoicePrefix) {
      setInvoicePrefix(preference.invoicePrefix);
    }
    if (preference.invoiceSeries) {
      setInvoiceSeries(preference.invoiceSeries);
    }
    if (preference.sellerInformation) {
      const sellerInformation = preference.sellerInformation;
      setOrganisationName(sellerInformation.organisationName);
      setAddressLine1(sellerInformation.addressLine1);
      setAddressLine2(sellerInformation.addressLine2);
      setState(sellerInformation.state);
      setCountry(sellerInformation.country);
      setEmail(sellerInformation.email);
      setTaxName(sellerInformation.taxName);
      setTaxId(sellerInformation.taxId);
      setTaxPercentage(sellerInformation.taxPercentage);
    }
  }, [invoicePreference]);

  // Save invoice preferences on backend
  const saveInvoicePreferences = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const payload = {
      autoSendInvoice,
      invoicePrefix,
      invoiceSeries,
      sellerInformation: {
        organisationName,
        addressLine1,
        addressLine2,
        state,
        country,
        email,
        taxId,
        taxName,
        taxPercentage,
      },
    };

    setSaveStatus("loading");
    updateInvoicePreference(
      payload,
      (preference) => {
        toast.addToast("Invoice preferences updated successfully");
        setSaveStatus("success");
      },
      (error) => {
        toast.addToast(
          "Error updating invoice preferences",
          "Try again later",
          ToastTypes.danger
        );
        setSaveStatus("error");
      }
    );
    analyticsService.track("invoice-details-updated", {
      invoiceSaveStatus: saveStatus
    })
  };

  if (!user) {
    return <LoginRequiredViewComponent />;
  }

  return (
    <div className="CommunityInvoicePage theme-bg-surface rounded mb-14">
      <div className="pb-10">
        <WidgetState state={invoicePreference.status} placeholder={<Loader />}>
          <form onSubmit={saveInvoicePreferences} className="px-4">
            <InvoiceSerialNumberComponent
              autoSendInvoice={autoSendInvoice}
              setAutoSendInvoice={setAutoSendInvoice}
              invoicePrefix={invoicePrefix}
              setInvoicePrefix={setInvoicePrefix}
              invoiceSeries={invoiceSeries}
              setInvoiceSeries={setInvoiceSeries}
            />
            <InvoiceSerialNumberInfoComponent
              organisationName={organisationName}
              setOrganisationName={setOrganisationName}
              addressLine1={addressLine1}
              setAddressLine1={setAddressLine1}
              addressLine2={addressLine2}
              setAddressLine2={setAddressLine2}
              state={state}
              setState={setState}
              country={country}
              setCountry={setCountry}
              email={email}
              setEmail={setEmail}
              taxId={taxId}
              setTaxId={setTaxId}
              taxName={taxName}
              setTaxName={setTaxName}
            />
            <InvoiceTaxCalcComponent
              taxPercentage={taxPercentage}
              setTaxPercentage={setTaxPercentage}
            />
            <Button
              label="Save"
              large
              className="mt-4"
              isLoading={saveStatus === "loading"}
            />
          </form>
        </WidgetState>
      </div>
    </div>
  );
}

/**
 * Contains information about invoice serial number
 */
function InvoiceSerialNumberComponent({
  autoSendInvoice,
  setAutoSendInvoice,
  invoicePrefix,
  setInvoicePrefix,
  invoiceSeries,
  setInvoiceSeries,
}: {
  autoSendInvoice: boolean;
  setAutoSendInvoice: (value: boolean) => void;
  invoicePrefix: string;
  setInvoicePrefix: (value: string) => void;
  invoiceSeries: string;
  setInvoiceSeries: (value: string) => void;
}) {
  return (
    <div className="py-4">
      {/* checkbox  */}
      <Checkbox
        selected={autoSendInvoice}
        onClick={() => setAutoSendInvoice(!autoSendInvoice)}
        children={
          <Label variant="t2" size="caption">
            Auto send invoice to billing email on membership purchase
          </Label>
        }
      />
      {/* Enter invoice serial number */}
      <div className="mt-6">
        <Label variant="t1">Enter Invoice serial number</Label>
        <div className=" mt-2 flex flex-row gap-3">
          <CTextInput
            label={"Prefix"}
            placeholder="ABC"
            defaultValue={invoicePrefix}
            onChange={setInvoicePrefix}
            labelSize="caption"
            labelVariant="t2"
            required
          />

          <CTextInput
            label={"Series"}
            placeholder="10000"
            defaultValue={invoiceSeries}
            onChange={setInvoiceSeries}
            type="number"
            labelSize="caption"
            labelVariant="t2"
            required
          />
        </div>
      </div>
      <div className="mt-2">
        <Label variant="s1" size="caption">
          Next Invoice numbers would be: {invoicePrefix}
          {SubscriptionUtils.getNextInvoiceSeries(invoiceSeries, 1)}, &nbsp;
          {invoicePrefix}
          {SubscriptionUtils.getNextInvoiceSeries(invoiceSeries, 2)}
        </Label>
      </div>
    </div>
  );
}

/**
 * Contains information about seller
 */
function InvoiceSerialNumberInfoComponent({
  organisationName,
  setOrganisationName,
  addressLine1,
  setAddressLine1,
  addressLine2,
  setAddressLine2,
  state,
  setState,
  country,
  setCountry,
  email,
  setEmail,
  taxName,
  setTaxName,
  taxId,
  setTaxId,
}: {
  organisationName: string;
  setOrganisationName: (value: string) => void;
  addressLine1: string;
  setAddressLine1: (value: string) => void;
  addressLine2: string;
  setAddressLine2: (value: string) => void;
  state: string;
  setState: (value: string) => void;
  country: string;
  setCountry: (value: string) => void;
  email: string;
  setEmail: (value: string) => void;
  taxName: string;
  setTaxName: (value: string) => void;
  taxId: string;
  setTaxId: (value: string) => void;
}) {
  return (
    <div className="py-6 px-4 theme-bg-default rounded">
      {/* header */}
      <div className="flex flex-row justify-between">
        <Label variant="t1">Seller information for invoice</Label>
        <a
          title="Download Sample Invoice"
          href="https://pensil-social.s3.ap-south-1.amazonaws.com/storage/documents/sample%20invoice%20pdf-9b20a8d6-036b-4043-a409-9437bb6f68ef.pdf">
          Download Sample Invoice
        </a>
      </div>

      {/* all input field */}

      <div className="mt-2 flex flex-col gap-2">
        <CTextInput
          required
          label="Org Name"
          placeholder="Community Name"
          defaultValue={organisationName}
          onChange={setOrganisationName}
        />
        <CTextInput
          label="Address Line 1"
          placeholder="Enter address"
          defaultValue={addressLine1}
          onChange={setAddressLine1}
          required
        />
        <CTextInput
          label="Address Line 2"
          placeholder="Enter address"
          defaultValue={addressLine2}
          onChange={setAddressLine2}
          required
        />
        <CTextInput
          label="State"
          placeholder="Enter state"
          defaultValue={state}
          onChange={setState}
          required
        />
        <CTextInput
          label="Country"
          placeholder="Enter country"
          defaultValue={country}
          onChange={setCountry}
          required
        />
        <CTextInput
          label="Email"
          type="email"
          placeholder="Enter email id"
          defaultValue={email}
          onChange={setEmail}
          required
        />
      </div>

      <div className="mt-2">
        <Label variant="t1">Tax Details</Label>
      </div>
      <div className="my-2 flex flex-row gap-2">
        <div className=" w-3/6">
          <CTextInput
            label="Tax name"
            placeholder="GST, VAT, etc"
            defaultValue={taxName}
            onChange={setTaxName}
            required
          />
        </div>
        <div className="w-full">
          <CTextInput
            label="ID"
            placeholder="Tax number"
            defaultValue={taxId}
            onChange={setTaxId}
            required
          />
        </div>
      </div>
      <Label variant="t2" size="caption">
        This will be displayed with seller info
      </Label>
    </div>
  );
}

function InvoiceTaxCalcComponent({
  taxPercentage,
  setTaxPercentage,
}: {
  taxPercentage: number;
  setTaxPercentage: (value: number) => void;
}) {
  return (
    <div className="my-6">
      <div className="mt-2 flex flex-col gap-2">
        <Label variant="t2" size="caption">
          {"Tax % (membership amount is inclusive of this tax%)"}
        </Label>
        <TextInput
          noMargin
          type="number"
          defaultValue={`${taxPercentage}`}
          onChange={(e) => setTaxPercentage(parseInt(e))}
          inputPattern={/^[0-9]+$/}
          placeholder="Enter percentage"
          large
          className="theme-bg-surface"
        />
      </div>
    </div>
  );
}

// Input component with label
interface TextInputProps {
  label: string;
  type?: string;
  placeholder?: string;
  className?: string;
  noMargin?: boolean;
  hidden?: boolean;
  readOnly?: string | null;
  required?: boolean;
  defaultValue?: string;
  onChange?: (e: any) => void;
  error?: string;
  inputPattern?: RegExp | undefined;
  labelVariant?: "t1" | "t2" | "s1" | "s2" | "cap";
  labelSize?: "h1" | "h2" | "h3" | "body1" | "body" | "caption" | "small";
}

function CTextInput({
  label,
  type = "text",
  placeholder,
  className,
  noMargin,
  required,
  labelVariant,
  hidden,
  readOnly,
  defaultValue,
  error,
  inputPattern,
  labelSize,
  onChange = () => {},
}: TextInputProps) {
  const isReadOnly = Validator.hasValue(readOnly);
  return (
    <div
      className={cx("flex flex-col gap-1", {
        hidden: hidden,
      })}>
      <Label size={labelSize} variant={labelVariant ?? "t1"}>
        {label}
      </Label>
      <TextInput
        type={type}
        noMargin
        readOnly={isReadOnly}
        disabled={isReadOnly}
        required={required}
        inputPattern={inputPattern}
        className={cx("theme-bg-surface", {
          "theme-text-subtitle-2 cursor-not-allowed": isReadOnly,
        })}
        defaultValue={defaultValue}
        placeholder={placeholder}
        onChange={(e) => onChange(e)}
        errors={[error]}
      />
    </div>
  );
}
export default CommunityInvoicePage;
